import Modal from '@components/common/Modal/Modal';
import SeasonPopList from '@components/page/SeasonPopList';
import { SeasonInfo, SeasonPopListItem } from '@/api/growth/season/type';
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/growth/season/api';
import { PhoneUtils } from '@utils/PhoneUtils';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';

interface SeasonHeaderProps {
    gsIdx?: number;
    onChangeIdx: (idx: number) => void;
}

const SeasonHeader = ({ gsIdx, onChangeIdx }: SeasonHeaderProps) => {
    const { isUser } = useAuthStore();

    const navigate = useNavigate();
    const [_gsIdx, _setGsIdx] = useState(gsIdx);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const openSeasonPop = (): void => {
        setIsModalOpen(true); // 이 줄이 빠져있었습니다!
    };

    const closeSeasonPop = (): void => {
        setIsModalOpen(false); // 이 줄이 빠져있었습니다!
    };

    const handleMoveToSeason = (): void => {
        navigate(`/season/${_gsIdx}`);
    };

    const handleRowClick = (row: SeasonPopListItem) => {
        _setGsIdx(row.gsIdx);
        onChangeIdx(row.gsIdx);
        closeSeasonPop();
    };

    const [season, setSeason] = useState<SeasonInfo | null>(null);

    const getSeason = useCallback(async () => {
        const result = await api.seasonInfo(Number(_gsIdx));
        setSeason(result);
    }, [_gsIdx]);

    useEffect(() => {
        if (_gsIdx && _gsIdx !== 0) {
            getSeason();
        }
    }, [getSeason, _gsIdx]);

    useEffect(() => {
        if (isUser() && !_gsIdx) {
            setIsModalOpen(true);
        }
    }, []);

    return (
        <>
            {isModalOpen && (
                <Modal
                    title={`${isUser() ? '작기선택' : '작기검색'}`}
                    size="xl"
                    onClose={closeSeasonPop}
                >
                    <SeasonPopList onClickRow={handleRowClick} />
                </Modal>
            )}

            <div className="tab-content">
                <div className=" p-0 border-bottom-0  border-0 mt-3">
                    <div className="custom-card card">
                        <div className="d-flex gap-5 align-items-center justify-content-between flex-wrap card-body">
                            <div className="d-flex align-items-center flex-wrap">
                                <div className="me-2">
                                    <span className="avatar avatar-lg avatar-rounded p-1 bg-info-transparent">
                                        <i className="ri-group-2-fill fs-40"></i>
                                    </span>
                                </div>
                                {season ? (
                                    <div className="flex-fill text-center">
                                        <span
                                            className="h5 d-block project-list-title mb-0 ms-3"
                                            data-discover="true"
                                        >
                                            {season.name}
                                        </span>
                                    </div>
                                ) : (
                                    <span>작기를 선택해주세요.</span>
                                )}
                            </div>
                            <div className="d-flex align-items-center flex-wrap">
                                {season ? (
                                    <div className="flex-fill text-center">
                                        <span
                                            className="fw-medium fs-14 d-block text-truncate project-list-title"
                                            data-discover="true"
                                        >
                                            {season.userName}
                                        </span>

                                        <span className="badge bg-success-transparent">
                                            {season.phone ? (
                                                <>
                                                    <i className="ri-phone-line" />{' '}
                                                    {PhoneUtils.format(
                                                        season.phone,
                                                    )}
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </span>
                                    </div>
                                ) : (
                                    <span>작기를 선택해주세요.</span>
                                )}
                            </div>
                            {season && (
                                <>
                                    <div className="avatar-list-stacked text-center">
                                        <div className="flex-fill">
                                            <span
                                                className="fs-14 d-block text-truncate project-list-title"
                                                data-discover="true"
                                            >
                                                ID : {season.userId}
                                            </span>
                                            <div className="d-flex">
                                                <span className="badge bg-primary-transparent me-1">
                                                    <i className="ri-pin-distance-fill"></i>{' '}
                                                    농지수 :{' '}
                                                    {season.farmlandCnt.toLocaleString()}
                                                    개
                                                </span>
                                                <span className="badge bg-pink-transparent">
                                                    <i className="ri-map-line"></i>{' '}
                                                    면적 :{' '}
                                                    {season.cultivationArea.toLocaleString()}{' '}
                                                    m²
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="fs-12">
                                            <span className="fw-semibold">
                                                작기시작:
                                            </span>{' '}
                                            {season.startDt}
                                        </div>
                                        <div className="fs-12">
                                            <span className="fw-semibold">
                                                작기종료:
                                            </span>{' '}
                                            {season.endDt}
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0 text-center">
                                        <div className="fs-12">
                                            <span className="fw-semibold">
                                                작기상태
                                            </span>
                                        </div>
                                        {season.isValid ? (
                                            <div className="badge bg-success">
                                                진행중
                                            </div>
                                        ) : (
                                            <div className="badge bg-danger">
                                                종료됨
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            <div className="d-flex gap-2 flex-wrap">
                                <div className="d-flex align-items-center">
                                    {season && (
                                        <button
                                            className="btn btn-info-light btn-sm"
                                            onClick={handleMoveToSeason}
                                        >
                                            <i className="ri-edit-line"></i>{' '}
                                            작기정보
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-orange-light ms-1 btn-sm task-delete-btn"
                                        onClick={openSeasonPop}
                                    >
                                        <i className="ri-search-line"></i>{' '}
                                        작기선택
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
export default SeasonHeader;
